import FuseScrollbars from "@fuse/core/FuseScrollbars";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { memo } from "react";
import Navigation from "@/components/theme-layouts/components/navigation/Navigation";
import UserMenu from "@/components/theme-layouts/components/UserMenu";
import { DEFAULT_BUSINESS_PIC, LOGIN_UNIFICADO } from "@/configs/runtime";
import useAutenticacaoJwt from "@auth/services/jwt/useAutenticacaoJwt";

// ------------------------------- CONSTANTS -------------------------------
const Root = styled("div")(({ theme }) => ({
	"--sidebar-logo-size": "112px",
	"--sidebar-font-size": "12px",
	"--sidebar-line-height": "16px",
	"--sidebar-item-height": "28px",
	"--sidebar-user-height": "44px",
	"--sidebar-avatar-size": "28px",
	"--sidebar-user-font-size": "13px",
	[`${theme.breakpoints.up("lg")} and (min-height: 900px)`]: {
		"--sidebar-logo-size": "120px",
		"--sidebar-font-size": "13px",
		"--sidebar-line-height": "18px",
	},
	background: [
		"radial-gradient(ellipse 160% 55% at 50% 100%, rgba(80,74,153,0.32) 0%, transparent 65%)",
		"radial-gradient(ellipse 90% 45% at 90% 15%, rgba(118,176,191,0.1) 0%, transparent 55%)",
		"linear-gradient(180deg, #030c1e 0%, #030d1c 10%, #002d5a 70%, #003770 100%)",
	].join(", "),
	color: theme.vars.palette.text.primary,
	"& ::-webkit-scrollbar-thumb": {
		boxShadow: "inset 0 0 0 20px rgba(255, 255, 255, 0.08)",
	},
	"& ::-webkit-scrollbar-thumb:active": {
		boxShadow: "inset 0 0 0 20px rgba(255, 255, 255, 0.16)",
	},
}));

const StyledContent = styled(FuseScrollbars)(() => ({
	overscrollBehavior: "contain",
	overflowX: "hidden",
	overflowY: "auto",
	WebkitOverflowScrolling: "touch",
	backgroundRepeat: "no-repeat",
	backgroundSize: "100% 40px, 100% 10px",
	backgroundAttachment: "local, scroll",
}));

// --------------------------------- TYPES ---------------------------------
type NavbarStyle1ContentProps = {
	readonly className?: string;
};

/**
 * The navbar style 1 content.
 */
// ------------------------------- COMPONENT -------------------------------
function NavbarStyle1Content(props: Readonly<NavbarStyle1ContentProps>) {
	// -------------------------------- STATES --------------------------------
	const { className = "" } = props;
	const { user, isAuthenticated } = useAutenticacaoJwt();

	// ----------------------------- DERIVED STATE ----------------------------
	const groupId = user?.id_cad_grupo;
	let businessPic = DEFAULT_BUSINESS_PIC;

	if (LOGIN_UNIFICADO) {
		businessPic = "";
		if (isAuthenticated && typeof groupId === "number" && Number.isSafeInteger(groupId) && groupId > 0)
			businessPic = `https://files.space.app.br/files/${groupId}/logo/logo_${groupId}.png`;
	}

	// -------------------------------- RETURN --------------------------------
	return (
		<Root className={clsx("flex h-full flex-auto flex-col overflow-hidden", className)}>
			<div
				style={{
					height: "3px",
					flexShrink: 0,
					background: "linear-gradient(90deg, #504a99, #e72b62)",
				}}
			/>
			{businessPic ? (
				<div>
					<img
						alt="Logo da empresa"
						src={businessPic}
						className="mx-auto my-2 h-(--sidebar-logo-size) w-(--sidebar-logo-size) object-contain rounded-xl"
					/>
				</div>
			) : null}

			<StyledContent
				className="flex min-h-0 flex-1 flex-col"
				option={{ suppressScrollX: true, wheelPropagation: false }}
			>
				<Navigation layout="vertical" dense />
			</StyledContent>

			<div className="flex min-w-0 flex-col overflow-hidden p-2">
				<UserMenu className="w-full min-w-0" />
			</div>
		</Root>
	);
}

export default memo(NavbarStyle1Content);
